import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { fetchAllAssetTransactionsForExport } from '../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { useReportingGridExport } from '../shared/table_grid_export/useReportingGridExport.ts'
import { useTabManagerContext } from '../../core/app-layout/lib/TabManagerContext.tsx'
import type { RootState } from '../../state_manager/store.ts'
import { getReportingGridColumnsForReportId } from '../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../state_manager/utils/reportingColumnCatalog.ts'
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'
import { useAssetTransactionsData } from './hooks/useAssetTransactionsData.ts'
import { useAssetTransactionsReportId } from '../../state_manager/hooks/useAssetTransactionsReportId.ts'
import { AssetTransactionsTable } from './components/AssetTransactionsTable.tsx'
import ViewToolbar from '../../core/components/ViewToolbar/index.tsx'
import { RIBBON_CTA_CLASS } from '../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../core/components/ViewConfig/index.ts'
import { TABLE_ASSET_TRANSACTIONS } from '../../state_manager/globals.ts'
import DisabledReason from '../../core/components/DisabledReason.tsx'
import { useCapability } from '../../core/hooks/useCapability.ts'

export default function AssetTransactionsPage() {
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const reportId = useAssetTransactionsReportId()
  const { openFormInTab } = useTabManagerContext()
  // docs/USER_RIGHTS_DESIGN.md 6.2: disabled with the reason when the user may not create
  const createRight = useCapability('asset_transactions.create')

  const {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableFilters,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useAssetTransactionsData()

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'asset_transactions' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of gridColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, gridColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const fetchAllForExport = useCallback(
    (token: string | null) => fetchAllAssetTransactionsForExport(activeSorting, activeFilters, token),
    [activeSorting, activeFilters]
  )
  const { exportLoading, handleExport } = useReportingGridExport({
    title: 'Asset Transactions',
    gridColumns,
    fetchAll: fetchAllForExport
  })

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey={TABLE_ASSET_TRANSACTIONS}
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) => handleFetch(activeSorting, Array.isArray(filters) ? filters : activeFilters)}
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={defaultTableFilters}
          onResetFilters={handleResetFilters}
          exportEnabled
          onExport={handleExport}
          exportLoading={exportLoading}
          beforeAddSlot={
            <>
            <ViewConfig
              moduleTitle='Asset Transactions'
              columns={gridColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Transaction', related: 'Related' }}
              columnLayout={columnLayout}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
            {/* Booking screen is a full form (no drawer), so the stock
                AddButton's drawer route doesn't apply - open the form tab. */}
            <DisabledReason reason={createRight.reason}>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                disabled={!createRight.allowed}
                onClick={() => openFormInTab(TABLE_ASSET_TRANSACTIONS, null)}
                className={RIBBON_CTA_CLASS}
              >
                Transaction
              </Button>
            </DisabledReason>
            </>
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <AssetTransactionsTable
            data={data}
            loading={dataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) => {
              const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
              handleSetFilters(filterArray)
            }}
            onSortChange={(sortConfig: any) => {
              if (sortConfig) {
                handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
              } else {
                handleSetSorting([])
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
